<script lang="ts" setup>
// @ts-ignore
import { kebabCase } from "scule";
import { resolveDynamicComponent } from "vue";
import cloneDeep from "lodash.clonedeep";
import {
  computed,
  createError,
  ref,
  useCurrentCulture,
  useHeadlessDataState,
  useHeadlessDataErrorState,
  useHeadlessResource,
  useRoute,
  useSeo,
  setTheme,
  watch,
} from "#imports";

definePageMeta( {
  middleware: "headless",
} );

const data = useHeadlessDataState();
const headlessError = useHeadlessDataErrorState();
const { getByRoute } = useHeadlessResource();
const culture = useCurrentCulture();
const route = useRoute();

if ( headlessError.value ) {
  const error = {
    statusCode: headlessError.value.statusCode,
    statusMessage: headlessError.value.statusMessage,
    cause: headlessError.value.cause,
    data: headlessError.value.data,
  };

  headlessError.value = null;

  throw createError( {
    ...error,
    fatal: true,
  } );
}

// We take a clone of the data as the middleware will change data.value
// for the current route as it's loading the new one.
// The clone will allow us to ignore those changes
const cloned = ref( cloneDeep( data.value ) );

if ( cloned.value?.seo ) {
  const seo = cloned.value.seo;
  useSeo( seo );
}

setTheme( cloned.value?.themeKey );

// Watch for culture changes and update the cloned + theme
watch( culture, async () => {
  const response = await getByRoute( route.path );

  if ( response.error.value ) {
    throw createError( {
      message: "Error changing content for culture",
      cause: response.error.value,
      fatal: true,
    } );
  }

  cloned.value = response.data.value;
  setTheme( cloned.value?.themeKey );
} );

/**
 * The prop data we'll pass to the template component
 */
const computedData = computed( () => {
  if ( cloned.value ) {
    return {
      [cloned.value.type]: cloned.value.data,
    };
  }

  return {};
} );

/**
 * The template component name
 */
const componentName = computed( () => {
  if ( cloned.value ) {
    return `lazy-template-${kebabCase( cloned.value.subType )}`;
  }

  return null;
} );

watch( componentName, ( val ) => {
  if ( val ) {
    const exists = typeof resolveDynamicComponent( val ) !== "string";
    if ( !exists ) {
      const err = { statusCode: 500, statusMessage: "Template not found: " + val, data: cloned.value?.subType, fatal: true };
      throw createError( err );
    }
  }
}, { immediate: true } );
</script>

<template>
  <component
    :is="componentName"
    v-bind="computedData"
    :key="componentName" />
</template>

<style scoped>

</style>
